/**
 * Billing entities — management invoices & SaaS billing.
 *
 * Mirror the schema in:
 *   supabase/migrations/20260706120000_create_management_invoices.sql
 *   supabase/migrations/20260706120100_create_saas_billing.sql
 *
 * Inlined here so this code compiles before src/integrations/supabase/types.ts
 * is regenerated. Once regenerated, replace with Database['public']['Tables'][…]['Row'].
 */

/** Status shared by every invoice-like row (management + saas), mirrors payments. */
export const INVOICE_STATUSES = [
  "pending",
  "paid",
  "overdue",
  "canceled",
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * How a management invoice is settled — mirrors
 * `management_invoices.settlement` (20260907110000).
 *
 *   transfer  the owner pays the invoice (collection_mode owner_collects).
 *   offset    the fee is deducted from the rent CoLiviq collected for the
 *             period (company_collects). Such an invoice carries NO due_date,
 *             so it can never read as overdue, and it is settled by recording
 *             a note about the deduction rather than by uploading a transfer
 *             proof — there is no transfer.
 */
export const INVOICE_SETTLEMENTS = ["transfer", "offset"] as const;
export type InvoiceSettlement = (typeof INVOICE_SETTLEMENTS)[number];

export type ManagementInvoice = {
  id: string;
  /** Human-facing number, e.g. "MF-2026-08-001". Assigned by DB trigger. */
  invoice_no: string | null;
  lessor_id: string | null;
  owner_user_id: string | null;
  property_id: number | null;
  apartment_id: number | null;
  /** First day of the covered month (YYYY-MM-01). */
  period_month: string;
  amount_cents: number;
  currency: string;
  /** How the amount was derived (revenue_model, fee pct/amount, rent base…). */
  fee_basis: Record<string, unknown>;
  description: string | null;
  status: InvoiceStatus;
  /**
   * How this fee gets settled. Defaults to "transfer" in the DB, so a row read
   * back from an older codepath (or a hand-written fixture) reads as an
   * ordinary payable invoice rather than accidentally as an offset one.
   */
  settlement: InvoiceSettlement;
  /**
   * When the owner must pay. Always NULL for a settlement="offset" invoice:
   * nothing is owed by transfer, and a due date is what would make
   * managementDisplayStatus report the owner as overdue on a fee already taken
   * out of their rent.
   */
  due_date: string | null;
  /**
   * Rechnungsdatum, frozen when the invoice is first sent to the owner. NULL =
   * not yet issued — the document renders today's date as a draft until then.
   */
  issue_date: string | null;
  paid_at: string | null;
  proof_url: string | null;
  proof_uploaded_at: string | null;
  invoice_pdf_url: string | null;
  sent_at: string | null;
  metadata: Record<string, unknown>;
  /**
   * COL-483: 0 for the month's first offset invoice, n for its n-th REMAINDER
   * (opened when invoice n-1 was closed with rent still outstanding).
   */
  remainder_seq?: number;
  /** COL-483: for a remainder, the invoice whose close opened it. */
  remainder_of?: string | null;
  created_at: string;
  updated_at: string;
};

export const SAAS_SUBSCRIPTION_STATUSES = [
  "active",
  "past_due",
  "canceled",
  "incomplete",
] as const;
export type SaasSubscriptionStatus =
  (typeof SAAS_SUBSCRIPTION_STATUSES)[number];

export type SaasSubscription = {
  id: string;
  subscriber_user_id: string | null;
  lessor_id: string | null;
  /** Owning operator org (COL-353); the entitlement gate keys on this. */
  operator_id: string | null;
  subscriber_name: string | null;
  plan: string;
  /** Monthly recurring revenue (MRR) for this subscription, in cents. */
  amount_cents: number;
  currency: string;
  status: SaasSubscriptionStatus;
  billing_day: number;
  started_at: string | null;
  canceled_at: string | null;
  /** Stripe Billing linkage (COL-352). */
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type SaasInvoice = {
  id: string;
  /** Human-facing number, e.g. "SAAS-2026-08-001". Assigned by DB trigger. */
  invoice_no: string | null;
  subscription_id: string | null;
  period_month: string;
  amount_cents: number;
  currency: string;
  status: InvoiceStatus;
  due_date: string | null;
  paid_at: string | null;
  proof_url: string | null;
  proof_uploaded_at: string | null;
  invoice_pdf_url: string | null;
  sent_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

/**
 * The single rule for what counts as a valid proof note: present after
 * trimming. Every surface (the upload dialogs, canMarkInvoicePaid, and — in
 * spirit — the DB `enforce_management_invoice_paid_gate` trigger) shares this
 * one definition so the note gate can't diverge per screen.
 */
export function isProofNotePresent(note: unknown): note is string {
  return typeof note === "string" && note.trim() !== "";
}

/**
 * A management invoice can only be marked paid once a proof has been uploaded
 * AND a mandatory note recorded about it. The note lives in metadata.proof_note
 * (no dedicated column). Keeping both rules in this shared predicate means every
 * caller — markPaid, any view, a future surface — enforces them identically,
 * instead of the note gate living only in a button's disabled state.
 */
export function canMarkInvoicePaid(row: {
  proof_url: string | null;
  status: InvoiceStatus;
  settlement?: InvoiceSettlement | null;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return (
    (isOffsetInvoice(row) || !!row.proof_url) &&
    isProofNotePresent(row.metadata?.proof_note) &&
    row.status !== "paid"
  );
}

/**
 * True when this invoice is settled by deducting the fee from rent CoLiviq
 * collected, rather than by the owner paying it.
 *
 * Read through this predicate rather than comparing the field, so the two
 * behaviours that follow from it — no proof file required to settle, and never
 * chased for payment — cannot drift apart per screen. Absent/unknown reads as
 * a transfer invoice, matching the DB default: assuming "offset" for a row we
 * are unsure about would silently stop chasing money that IS owed.
 */
export function isOffsetInvoice(row: {
  settlement?: InvoiceSettlement | null;
}): boolean {
  return row.settlement === "offset";
}

/**
 * Where a payout confirmation records the net amount it paid out, in cents:
 * `management_invoices.metadata.net_payout_cents`, written by confirmOffset in
 * the same update that marks the invoice paid. No column: the invoice's
 * metadata already carries the confirmation note beside it. Absent on every
 * invoice confirmed before COL-516, and on one confirmed where no live payout
 * figure was available.
 */
export const OFFSET_NET_PAYOUT_METADATA_KEY = "net_payout_cents";

/**
 * Whether an invoice's own figures (amount, due date) may still be changed.
 *
 * Two reasons they may not, and the second is a legal one:
 *
 *  - `sent_at` — the invoice has been issued to the owner. There is no stored
 *    copy of what was sent (`invoice_pdf_url` is never written for management
 *    invoices; the document is re-rendered from this row on every view and
 *    re-send), so editing the amount would silently restate an already-issued
 *    invoice_no — the owner's email and the system would disagree, with no
 *    record of the original. GoBD requires an issued invoice to be
 *    unveränderbar, and §14 UStG / §31 Abs. 5 UStDV corrects one by cancelling
 *    and reissuing (Storno + new number), not by amending it in place. Cancel
 *    the invoice (status 'canceled') and issue a new one instead.
 *  - `paid` — the recorded proof attests to what was actually settled; editing
 *    the amount underneath it would leave the two disagreeing. Unmark first.
 *
 * Shared so every surface enforces the same rule, and mirrored by the DB
 * trigger `enforce_management_invoice_immutable_after_send` — a hidden button
 * is a courtesy, the trigger is the control.
 */
export function canEditManagementInvoice(row: {
  status: InvoiceStatus;
  sent_at: string | null;
}): boolean {
  return row.status !== "paid" && !row.sent_at;
}

/**
 * Whether an invoice can be corrected by cancelling and reissuing it — the only
 * route by which an ISSUED invoice's figures ever change (see
 * canEditManagementInvoice for why they can't change in place).
 *
 * Exactly complementary to that predicate on the cases that matter: an unissued
 * draft is edited, an issued invoice is cancelled and reissued, and never both.
 * A paid invoice is neither — the money arrived, so a correction is a refund or
 * a credit note; unmark it first if the payment itself was recorded in error.
 *
 * Mirrored by cancel_and_reissue_management_invoice(), which re-checks all three
 * conditions server-side.
 */
export function canCancelAndReissueManagementInvoice(row: {
  status: InvoiceStatus;
  sent_at: string | null;
}): boolean {
  return row.status !== "paid" && row.status !== "canceled" && !!row.sent_at;
}
